import { StrictMode, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, createRoutesFromElements, RouterProvider } from 'react-router-dom'
import { readWorkspaceBasename } from './lib/workspace-url'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { HelmetProvider } from 'react-helmet-async'
import { ErrorBoundary } from './components/ErrorBoundary'
import { FeedbackProvider } from './components/ui/FeedbackProvider'
import { ApiError, setSignedOutHandler } from './lib/api-client'
import { readSsrPayload } from './ssr-payload'
// Side-effect import — bootstraps i18next before any component reads strings.
import './i18n'
// Global design tokens MUST load before every other stylesheet — index.css,
// vendor-panel.css, super-admin.css and the Tailwind theme all derive from
// these variables. See docs/design-system/README.md.
import './styles/design-tokens.css'
import './styles/shadcn-theme.css'
import './index.css'

/**
 * AppRouter — builds the data router lazily, on mount, instead of importing
 * `./routes/router` (and the ~80 lazy() page chunks it declares) up front.
 *
 * This used to be `const AppRoutes = lazy(() => import('./routes/router')...)`
 * rendered under a plain `<Suspense>` — React.lazy can't be used here any
 * more because `createBrowserRouter` needs the route config as a plain value,
 * not a lazily-resolved component, but the reason for deferring the import is
 * unchanged: keeps the router's own module graph (every vendor panel chunk)
 * out of the bundle unauthenticated visitors pay for on first paint.
 */
function AppRouter({ basename }: { basename: string }) {
  const [router, setRouter] = useState<ReturnType<typeof createBrowserRouter> | null>(null)

  useEffect(() => {
    let cancelled = false
    import('./routes/router').then((m) => {
      if (cancelled) return
      setRouter(
        createBrowserRouter(createRoutesFromElements(m.appRouteElements()), { basename }),
      )
    })
    return () => {
      cancelled = true
    }
  }, [basename])

  if (!router) return <RoutesLoader />
  return <RouterProvider router={router} />
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 402 is a plan decision (read-only workspace, feature not on the
      // plan) — retrying cannot change it and would repeat the plan toast.
      retry: (failureCount, error) => !(error instanceof ApiError && error.status === 402) && failureCount < 2,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
})

// Signing out drops every cached read: a tokened URL or another user's records must not survive it.
setSignedOutHandler(() => queryClient.clear())

function RoutesLoader() {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-slate-50">
      <div className="text-sm text-slate-500">Loading…</div>
    </div>
  )
}

/**
 * Workspace prefix (URL-key remediation, 2026-08-22).
 *
 * Read ONCE, before the router mounts, from the URL we were loaded with. Every
 * `navigate('/leads')` and `to="/leads"` in the app then resolves under
 * `/w/<slug>` automatically — react-router adds the basename on the way out and
 * strips it on the way in — so none of the 141 path literals across the app had
 * to change.
 *
 * Public pages (/login, /p/<token>, /pay/<token>, the landing, CMS pages) carry
 * no prefix and get `''`, which is react-router's no-op basename. Switching
 * workspace therefore requires a real document navigation, not a client-side
 * one: see WorkspaceUrlSync in routes/router.tsx.
 */
const workspaceBasename = readWorkspaceBasename(window.location.pathname)

/**
 * Stale-tab recovery for lazy page chunks.
 *
 * An open tab can lose access to /assets/* in two ways: the nginx site-gate
 * cookie expires (2h → every chunk answers 401), or a redeploy replaces the
 * hashed chunk files (→ 404). Either way the next client-side navigation dies
 * on "Failed to fetch dynamically imported module" and react-router shows its
 * raw error page. A full document load fixes both: nginx sends an expired
 * visitor to /gate?next=<this path>, and a stale tab picks up the new
 * index.html. The timestamp guard stops a genuinely missing chunk from
 * reloading forever — the second failure inside the window falls through to
 * the normal error page.
 */
const CHUNK_RELOAD_KEY = 'xq:chunk-reload-at'
const CHUNK_RELOAD_WINDOW_MS = 10_000
window.addEventListener('vite:preloadError', (event) => {
  let lastReloadAt: number
  try {
    lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
  } catch {
    // Storage blocked: without the guard a missing chunk would reload forever, so show the error.
    return
  }
  if (Date.now() - lastReloadAt < CHUNK_RELOAD_WINDOW_MS) return
  event.preventDefault()
  window.location.reload()
})

// Server-rendered marketing page → hydrate the marketing tree only. The
// payload is written by the backend SSR renderer (services/ssr/marketing-ssr.ts)
// and never appears on app pages, so this branch cannot affect the app.
const ssrPayload = readSsrPayload()
if (ssrPayload) {
  void import('./entry-client').then((m) => m.hydrateMarketing(ssrPayload))
} else {
  bootApp()
}

function bootApp() {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ErrorBoundary>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            <FeedbackProvider>
              <AppRouter basename={workspaceBasename} />
            </FeedbackProvider>
          </QueryClientProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </StrictMode>,
  )
}
